import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from '../login.service';
import { take, takeUntil } from 'rxjs/operators';
import { race, Subject, timer } from 'rxjs';
import { PatientsService } from '../patients.service';
import { ExamsService } from '../exams.service';

@Component({
  selector: 'app-patient-redirect',
  templateUrl: './patient-redirect.component.html',
  styleUrls: ['./patient-redirect.component.scss'],
  standalone: false,
})
export class PatientRedirectComponent implements OnInit, OnDestroy {
  code;
  showPatientSearch = false;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    public ls: LoginService,
    public patientsService: PatientsService,
    public examsService: ExamsService,
  ) {}

  ngOnInit(): void {
    this.ls.onFailedLogin.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.router.navigate(["/"]);
    });

    this.route.params.subscribe((params) => {
      const mode = this.route.snapshot.data?.mode || 'loginCode';
      const target = this.route.snapshot.data?.target || 'patient';

      this.ls.onLogin.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.navigateToTarget(target, params);
      });

      if (mode === 'restoreSession') {
        this.code = params['code'];
        this.http.post('/api/newExams/resurection', { code: this.code }).subscribe(
          () => {
            this.ls.followLogin(this.http.post('/api/login', { retry: true }), false);
          },
          () => {
            this.router.navigate(['/']);
          }
        );
        return;
      }

      if (params['code']) {
        this.code = params['code'];
        this.ls.login(undefined, undefined, false, false, undefined, this.code);
      } else {
        this.ls.loginMissing();
        this.showPatientSearch = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private navigateToTarget(target: string, params: any) {
    // This route is a bare sibling of the Workspace shell (see
    // app-routing.module), so rendering the patient list in place comes up
    // without the side bar. Hand off to the real in-shell route instead.
    //
    // Wait for onLoginComplete first: onLogin fires before /api/login/getTOS
    // resolves, and the destination gates work on `premissions` (the
    // Consent-guarded practitioners fetch in PatientsComponent), which would
    // silently skip. The timer is a safety net so a login that never
    // reaches getTOS (hospital installs) still navigates instead of hanging.
    race(this.ls.onLoginComplete.pipe(take(1)), timer(5000))
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (target === 'exam') {
          this.openExam(params);
        } else if (params['pid']) {
          this.patientsService.getPatient(params['pid'], (data: boolean) => {
            if (data) {
              this.router.navigate(['/exams'], {
                state: { data: this.patientsService.currentPatient },
              });
            }
          });
        } else {
          this.router.navigate(['/patsearch']);
        }
      });
  }

  private openExam(params: any) {
    const pid = Number(params["pid"]);
    const examId = Number(params["examId"]);
    const pregIndex = params["pregIndex"] !== "null" ? Number(params["pregIndex"]) : null;
    const examType = this.route.snapshot.queryParamMap.get("examType") || "";

    if (!pid || !examId || !examType) {
      this.router.navigate(["/"]);
      return;
    }

    this.patientsService.getPatient(pid, (data: boolean) => {
      if (!data) {
        this.router.navigate(["/"]);
        return;
      }

      const preg = pregIndex ? { AutoIndex: pregIndex } : null;
      const exam = {
        AutoIndex: examId,
        PID: pid,
        PregIndex: pregIndex,
        ExamType: examType,
      };

      this.examsService.goExam(preg, exam, examType, true);
    });
  }
}
